import { useEffect, useRef } from "react";
import { sessionModalTracker } from "../Utils/sessionModalTracker";

/**
 * Hook for registering session-critical modals with the session tracker.
 *
 * Session-critical modals are UI flows that should not be interrupted by
 * JWT session expiry (e.g., eKYC verification, document signing).
 *
 * When a modal is open and registered, the JWT expiry hook will force logout
 * even on the login page, ensuring security while the modal flow completes.
 *
 * USAGE:
 * ```tsx
 * const MyModal: FC<{ opened: boolean; onClose: () => void }> = ({ opened, onClose }) => {
 *   useSessionCriticalModal('my-critical-modal', opened);
 *
 *   return (
 *     <Modal opened={opened} onClose={onClose}>
 *       {/* modal content *\/}
 *     </Modal>
 *   );
 * };
 * ```
 *
 * @param modalId - Unique identifier for this modal type
 * @param isOpen - Whether the modal is currently open
 * @returns Object containing isRegistered state (for debugging)
 */
export function useSessionCriticalModal(
  modalId: string,
  isOpen: boolean
): { isRegistered: boolean } {
  // Track if currently registered to return accurate state
  const isRegisteredRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      sessionModalTracker.register(modalId);
      isRegisteredRef.current = true;
    } else {
      sessionModalTracker.unregister(modalId);
      isRegisteredRef.current = false;
    }

    // Cleanup: ensure we unregister when component unmounts
    return () => {
      if (isRegisteredRef.current) {
        sessionModalTracker.unregister(modalId);
        isRegisteredRef.current = false;
      }
    };
  }, [modalId, isOpen]);

  return { isRegistered: isRegisteredRef.current };
}

export default useSessionCriticalModal;
